import { memo, useEffect, useRef } from "react";
import { animate, useMotionValue, useMotionValueEvent } from "motion/react";
import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from "@xyflow/react";
import type { BatonEdge } from "./model.js";
import { describeActivity } from "./activity.js";
import { usePrefersReducedMotion } from "./reduced-motion.js";
import { useFlowActions } from "./nodes.js";

/**
 * One edge type draws two things: the durable scope, assignment or wire relationship,
 * and a short-lived directional packet for authorized activity. The packet carries a
 * routing state and nothing else; the message itself stays ciphertext until an
 * authorized thread is opened in the crypto worker. A wire also carries a "chat" pill
 * that opens the conversation between its two agents.
 */
function AnimatedMessageEdge(props: EdgeProps<BatonEdge>) {
  const actions = useFlowActions();
  const [path, labelX, labelY] = getBezierPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    sourcePosition: props.sourcePosition,
    targetX: props.targetX,
    targetY: props.targetY,
    targetPosition: props.targetPosition,
    curvature: 0.32,
  });

  const event = props.data?.event ?? null;
  const relation = props.data?.relation ?? "scope";
  const presentation = event ? describeActivity(event.state) : null;
  const staticOnly = usePrefersReducedMotion();

  const pathRef = useRef<SVGPathElement | null>(null);
  const packetRef = useRef<SVGGElement | null>(null);
  const sweepRef = useRef<SVGPathElement | null>(null);
  const progress = useMotionValue(0);
  const sweep = useMotionValue(1);

  useMotionValueEvent(progress, "change", (value) => {
    const geometry = pathRef.current;
    const packet = packetRef.current;
    if (!geometry || !packet) return;
    const total = geometry.getTotalLength();
    if (!Number.isFinite(total) || total === 0) return;
    const distance = Math.min(Math.max(value, 0), 1) * total;
    const point = geometry.getPointAtLength(distance);
    const ahead = geometry.getPointAtLength(Math.min(total, distance + 2));
    const angle = (Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180) / Math.PI;
    packet.setAttribute("transform", `translate(${point.x} ${point.y}) rotate(${angle})`);
  });

  useMotionValueEvent(sweep, "change", (value) => {
    sweepRef.current?.setAttribute("stroke-dashoffset", String(value));
  });

  useEffect(() => {
    if (!event || !presentation) return;
    if (staticOnly || !presentation.travels) {
      // Reduced motion, blocked and failed all resolve to a fixed marker on the path.
      progress.set(presentation.travelTo);
      sweep.set(0);
      return;
    }
    progress.set(0);
    sweep.set(1);
    const travel = animate(progress, presentation.travelTo, {
      duration: presentation.durationMs / 1_000,
      ease: event.state === "completed" ? [0.16, 1, 0.3, 1] : [0.4, 0, 0.2, 1],
    });
    const drawn = event.state === "completed"
      ? animate(sweep, 0, { duration: 0.55, ease: [0.16, 1, 0.3, 1] })
      : null;
    return () => {
      travel.stop();
      drawn?.stop();
    };
  }, [event, presentation, progress, staticOnly, sweep]);

  const tone = presentation?.tone ?? "neutral";
  const packetShown = event !== null;
  const wire = relation === "wire" && props.data?.refId && props.data.sourceAgentId && props.data.targetAgentId
    ? { wireId: props.data.refId, sourceAgentId: props.data.sourceAgentId, targetAgentId: props.data.targetAgentId }
    : null;

  return <>
    <BaseEdge
      id={props.id}
      path={path}
      className={`baton-edge baton-edge-${relation}${packetShown ? ` baton-edge-active tone-${tone}` : ""}${props.selected ? " is-selected" : ""}`}
      style={props.style ?? {}}
    />
    <path ref={pathRef} d={path} fill="none" stroke="none" pointerEvents="none" aria-hidden="true" />
    {event?.state === "completed" && <path
      ref={sweepRef}
      className="baton-edge-sweep"
      d={path}
      fill="none"
      pathLength={1}
      strokeDasharray="1 1"
      strokeDashoffset={staticOnly ? 0 : 1}
      pointerEvents="none"
      aria-hidden="true"
    />}
    {packetShown && <g
      ref={packetRef}
      className={`baton-packet tone-${tone}${staticOnly ? " is-static" : ""}`}
      pointerEvents="none"
      aria-hidden="true"
    >
      <rect x="-11" y="-5" width="22" height="10" rx="5" className="baton-packet-body" />
      <rect x="-4" y="-1.5" width="8" height="3" rx="1.5" className="baton-packet-seal" />
    </g>}
    {packetShown && presentation && <EdgeLabelRenderer>
      <div
        className={`baton-edge-label tone-${tone}`}
        style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
        data-testid={`edge-state-${props.id}`}
      >
        <span aria-hidden="true">{presentation.glyph}</span>
        <span>{presentation.label}</span>
      </div>
    </EdgeLabelRenderer>}
    {wire && !packetShown && <EdgeLabelRenderer>
      <button
        type="button"
        className="baton-wire-pill nodrag nopan"
        style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
        onClick={() => actions.openWire(wire)}
        data-testid={`wire-chat-${wire.wireId}`}
        aria-label="Open the conversation on this wire"
      >chat</button>
    </EdgeLabelRenderer>}
  </>;
}

export const BATON_EDGE_TYPES = { batonMessage: memo(AnimatedMessageEdge) };
